import React, { useEffect, useState, useContext, useCallback, useRef } from "react";
import { Container } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import APIServicenew from "../utils/APIGeneralService";
import { UserContext } from "../context/UserContextProvider";
import { formatDateTime } from "../utils/Helper";
import PaginatedTable from "../components/Table/PaginatedTable";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TestRunView = () => {
  const APIServices = new APIServicenew(process.env.REACT_APP_OTHER_API_URL);
  const { accessToken, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const { runId } = useParams();

  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  /* ================= State ================= */
  const [summary, setSummary] = useState({
    total_tests: 0,
    duration: "0m 0s",
    pass_rate: "0%",
    failed_count: 0
  });
  const [testCases, setTestCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [totalTestCases, setTotalTestCases] = useState(0);
  const [runName, setRunName] = useState("Test Run Details");
  const [actionLoading, setActionLoading] = useState({});
  // For debouncing
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [totalDuration, setTotalDuration] = useState(0);
  const debounceTimeoutRef = useRef(null);

  /* ================= Debouncing Effect ================= */
  useEffect(() => {
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to page 1 when search term changes
      if (searchTerm !== debouncedSearchTerm) {
        setPage(1);
      }
    }, 300); // 300ms delay

    // Cleanup on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm]);

  /* ================= Status Options ================= */
  const statusOptions = [
    { value: "", label: "Status" },
    { value: "NOT_STARTED", label: "NOT STARTED" },
    { value: "IN_PROGRESS", label: "IN PROGRESS" },
    { value: "PASSED", label: "PASSED" },
    { value: "FAILED", label: "FAILED" },
    { value: "SKIPPED", label: "SKIPPED" },
    { value: "PAUSED", label: "PAUSED" }
  ];

  /* ================= Helper Functions ================= */
  const formatStatusDisplay = (status) => {
    if (!status) return "NOT STARTED";
    return status.replace(/_/g, ' ');
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'PASSED':
        return 'completed';
      case 'FAILED':
        return 'failed';
      case 'IN_PROGRESS':
        return 'inprogress';
      case 'SKIPPED':
        return 'pending';
      case 'NOT_STARTED':
        return 'pending';
      case 'PAUSED':
        return 'paused';
      default:
        return 'pending';
    }
  };

  /* ================= API Functions ================= */
  const fetchTestRunDetails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        run_id: runId,
        limit: limit,
        page: page
      });

      // Use debounced search term
      if (debouncedSearchTerm) {
        params.append('q', debouncedSearchTerm.trim());
      }

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await APIServices.get(
        `test-execution/runs/cases?${params.toString()}`,
        headers
      );

      if (response?.status === 200) {



        setSummary(response.data.summary || {
          total_tests: 0,
          duration: "0m 0s",
          pass_rate: "0%",
          failed_count: 0
        });
        setTestCases(response.data.test_cases || []);
        setTotalTestCases(response.data.summary?.total_tests || 0);

        const maxDurationInSeconds = response?.data?.test_cases.reduce((max, test) => {
          const seconds = parseInt(test.duration?.replace("s", ""), 10) || 0;
          return seconds > max ? seconds : max;
        }, 0);

        const minutes = Math.floor(maxDurationInSeconds / 60);
        const seconds = maxDurationInSeconds % 60;

        setTotalDuration(`${minutes}m ${seconds}s`);


        if (response.data.summary?.run_name) {
          setRunName(response.data.summary.run_name);
        }
      } else if (response?.status === 401) {
        logout();
        navigate("/");
      }
    } catch (error) {
      console.error("Test run view error:", error);
    } finally {
      setLoading(false);
    }
  }, [runId, page, limit, debouncedSearchTerm, statusFilter, accessToken]);

  useEffect(() => {
    fetchTestRunDetails();
  }, [fetchTestRunDetails]);

  /* ================= Event Handlers ================= */
  const handleApplyFilters = () => {
    // Clear any existing debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    // Apply filters immediately
    setDebouncedSearchTerm(searchTerm);
    setPage(1);
  };

  const handleResetFilters = () => {
    // Clear debounce timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    setSearchTerm("");
    setDebouncedSearchTerm("");
    setStatusFilter("");
    setPage(1);
  };

  const handleExportResults = () => {
    console.log("Exporting results...");
    // Implement export functionality
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      // Clear debounce timeout and apply immediately
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      handleApplyFilters();
    }
  };

  // Function to handle immediate search (when user stops typing)
  const handleSearchChange = (value) => {
    setSearchTerm(value);
    // Don't set debouncedSearchTerm here - let the useEffect handle it
  };

  /* ================= Test Case Action Handler ================= */
  /* ================= Test Case Action Handler ================= */
  const handleTestCaseAction = async (testCase, actionType, currentStatus) => {
    try {
      const testCaseId = testCase.test_case_id || testCase.id;
      const executionId = testCase.test_execution_id; // Get from your test case data

      // Set loader only for clicked action
      setActionLoading((prev) => ({
        ...prev,
        [testCaseId]: {
          ...prev[testCaseId],
          [actionType]: true,
        },
      }));

      // Determine the action based on current status if needed
      let action = actionType;
      if (actionType === 'toggle') {
        action = currentStatus === 'PAUSED' ? 'resume' : 'pause';
      }

      const payload = {
        action: action, // "pause", "resume", "stop"
        runExecutionId: `RUN-${runId}`, // From URL params
        executionId: executionId, // test_execution_id from list API
        isFolder: "False",
      };

      console.log("Sending payload:", payload); // For debugging

      const response = await APIServices.post(
        "test-execution/action",
        payload,
        headers
      );

      if (response?.status === 200 || response?.status === 201) {
        toast.success(`Test case ${action} successfully`);
        // Refresh the test cases to get updated status
        fetchTestRunDetails();
      } else {
        toast.error(response?.data?.detail ?? `Failed to ${action} test case`);
      }
    } catch (error) {
      toast.error(error.message || `Error ${actionType}ing test case`);
      console.error(`Error ${actionType}ing test case:`, error);
    } finally {
      // Remove loader only for that action
      const testCaseId = testCase.test_case_id || testCase.id;
      setActionLoading((prev) => ({
        ...prev,
        [testCaseId]: {
          ...prev[testCaseId],
          [actionType]: false,
        },
      }));
    }
  };

  const handleViewTestCase = (testCase) => {
  const testCaseId = testCase.test_case_id || testCase.id;
  const executionId = testCase.test_execution_id;

  // Navigate to images page with state
  navigate(`/admin/test-cases-images/${runId}`, {
    state: {
      runId: runId,
      executionId: executionId
    }
  });
};

const handleDownloadTestCase = async (testCase) => {
  try {
    const testCaseId = testCase.test_case_id || testCase.id;
    const executionId = testCase.test_execution_id;
    const baseURL =  process.env.REACT_APP_OTHER_API_URL;
    const downloadUrl = `${baseURL}gcp/download-folder?folder=RUN-${runId}/${executionId}`;
    setActionLoading((prev) => ({
      ...prev,
      [testCaseId]: {
        ...prev[testCaseId],
        download: true,
      },
    }));
    window.open(downloadUrl);
    toast.success('Download started');
    
  } catch (error) {
    toast.error('Failed to download file');
  } finally {
    const testCaseId = testCase.test_case_id || testCase.id;
    setActionLoading((prev) => ({
      ...prev,
      [testCaseId]: {
        ...prev[testCaseId],
        download: false,
      },
    }));
  }
};
  /* ================= Table Columns ================= */
  const columns = [
    {
      dataField: "index",
      text: "S.NO",
      formatter: (cell, row, rowIndex) => (page - 1) * limit + rowIndex + 1,
    },
    {
      dataField: "test_case_id",
      text: "Test Case ID",
    },
    {
      dataField: "test_case_name",
      text: "Test Case Name",
    },
    {
      dataField: "start_time",
      text: "Start Time",
      formatter: (cell) => (cell ? formatDateTime(cell) : "-"),
    },
    {
      dataField: "end_time",
      text: "End Time",
      formatter: (cell) => (cell ? formatDateTime(cell) : "-"),
    },
    {
      dataField: "duration",
      text: "Duration",
      formatter: (cell) => cell || "0s",
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => {
        const normalizedStatus = cell
          ?.toString()
          .toLowerCase()
          .replace(/\s+/g, "_");

        const isInProgress = normalizedStatus === "in_progress";
        const isPaused = normalizedStatus === "paused";

        if (isInProgress) {
          return (
            <span
              className={`d-inline-flex align-items-center status ${getStatusClass(cell)}`}
              style={{ opacity: 0.6 }}
            >
              <span className="spinner-border spinner-border-sm me-2" />
              <span>Running...</span>
            </span>
          );
        } else if (isPaused) {
          return (
            <span className={`status status-paused`}>
              PAUSED
            </span>
          );
        } else {
          return (
            <span className={`status ${getStatusClass(cell)}`}>
              {formatStatusDisplay(cell)}
            </span>
          );
        }
      },
    },
    {
      dataField: "error_message",
      text: "Error Message",
      formatter: (cell) => (
        <div
          style={{
            maxWidth: "300px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
            lineHeight: "1.4",
          }}
        >
          {cell || "-"}
        </div>
      ),
    },
    {
      dataField: "screenshot",
      text: "Screenshot",
      formatter: (cell, row) => {
        const normalizedStatus = row.status
          ?.toString()
          .toLowerCase()
          .replace(/\s+/g, "_");

        const isRunning = normalizedStatus === "in_progress" || normalizedStatus === "running";
        const isPaused = normalizedStatus === "paused";

        // Get loading states for this test case
        const testCaseId = row.test_case_id || row.id;
        const pauseLoading = actionLoading[testCaseId]?.pause ||
          actionLoading[testCaseId]?.resume ||
          actionLoading[testCaseId]?.toggle;
        const stopLoading = actionLoading[testCaseId]?.stop;
        const viewLoading = actionLoading[testCaseId]?.view;
        const downloadLoading = actionLoading[testCaseId]?.download;

        // Show controls for running/paused states
        if (isRunning || isPaused) {
          return (
            <div className="d-flex gap-2 align-items-center">-</div>
          );
        }
        else {
          return (
            <div className="d-flex gap-2 align-items-center">
              {/* View button with loading state */}
              {viewLoading ? (
                <button className="btn btn-sm btn-secondary" disabled>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Downloading...
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-info text-white"
                  onClick={() => {
                    setActionLoading((prev) => ({
                      ...prev,
                      [testCaseId]: {
                        ...prev[testCaseId],
                        view: true,
                      },
                    }));
                    handleViewTestCase(row);
                    // Remove loading after navigation
                    setTimeout(() => {
                      setActionLoading((prev) => ({
                        ...prev,
                        [testCaseId]: {
                          ...prev[testCaseId],
                          view: false,
                        },
                      }));
                    }, 500);
                  }}
                  title="View Test Case Images"
                >
                  <i className="fa-solid fa-eye me-1"></i>
                  View
                </button>
              )}

              {/* Download button with loading state */}
              {downloadLoading ? (
                <button className="btn btn-sm btn-secondary" disabled>
                  <span className="spinner-border spinner-border-sm me-1" />
                  Downloading...
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => handleDownloadTestCase(row)}
                  title="Download Results"
                >
                  <i className="fa-solid fa-download me-1"></i>
                  Download
                </button>
              )}
            </div>
          );
        }
      },
    },
    {
      dataField: "actions",
      text: "Action",
      formatter: (cell, row) => {
        const normalizedStatus = row.status
          ?.toString()
          .toLowerCase()
          .replace(/\s+/g, "_");

        const isRunning = normalizedStatus === "in_progress" || normalizedStatus === "running";
        const isPaused = normalizedStatus === "paused";

        // Get loading states for this test case
        const testCaseId = row.test_case_id || row.id;
        const pauseLoading = actionLoading[testCaseId]?.pause ||
          actionLoading[testCaseId]?.resume ||
          actionLoading[testCaseId]?.toggle;
        const stopLoading = actionLoading[testCaseId]?.stop;
        const viewLoading = actionLoading[testCaseId]?.view;
        const downloadLoading = actionLoading[testCaseId]?.download;

        // Show controls for running/paused states
        if (isRunning || isPaused) {
          return (
            <div className="d-flex gap-2 align-items-center">
              {/* Pause / Resume button */}
              {pauseLoading ? (
                <button className="btn btn-sm btn-secondary" disabled>
                  <span className="spinner-border spinner-border-sm" />
                </button>
              ) : (
                <button
                  className={`btn btn-sm ${isPaused ? 'btn-success' : 'btn-warning'}`}
                  onClick={() => handleTestCaseAction(
                    row,
                    'toggle',
                    row.status
                  )}
                  title={isPaused ? "Resume" : "Pause"}
                  disabled={pauseLoading}
                >
                  <i className={`fa-solid ${isPaused ? "fa-play" : "fa-pause"}`}></i>
                </button>
              )}

              {/* Stop button */}
              {stopLoading ? (
                <button className="btn btn-sm btn-secondary" disabled>
                  <span className="spinner-border spinner-border-sm" />
                </button>
              ) : (
                <button
                  className="btn btn-sm btn-danger"
                  onClick={() => handleTestCaseAction(row, 'stop', row.status)}
                  title="Stop"
                  disabled={stopLoading}
                >
                  <i className="fa-solid fa-stop"></i>
                </button>
              )}
            </div>
          );
        }
      },
    },
  ];

  /* ================= JSX ================= */
  return (
    <div className="all_content test_run_management test_run_view">
      <Container fluid>
        {/* ===== Header ===== */}
        <div className="head-section d-flex align-items-center justify-content-between">
          <h1 className="font_16 font-600 clr_blue">
            {runName}
          </h1>

          <div className="d-flex gap-2">
            <button
              className="btn d-flex align-items-center gap-2 font_14 clr_sky"
              onClick={() => navigate(-1)}
            >
              <i className="fas fa-long-arrow-alt-left"></i> Back to Test Run
            </button>
          </div>
        </div>

        <p className="font_14 clr_grey mt-4">
          Detailed results for the recent test execution.
        </p>

        {/* ===== Stats ===== */}
        <div className="stats_section mt-4">
          <div className="row g-3">
            <StatCard title="Total Tests" value={summary.total_tests || 0} color="bg-blue" />
            <StatCard title="Duration" value={totalDuration || "0m 0s"} color="bg-purple" />
            <StatCard title="Pass Rate" value={summary.pass_rate || "0%"} color="bg-green" />
            <StatCard title="Failed" value={summary.failed_count || 0} color="bg-orange" />
          </div>
        </div>

        {/* ===== Table Card ===== */}
        <div className="comman_table user_management_table mt-4">
          {/* Search and Filter Section */}
          <div className="search-box d-flex align-items-center gap-3 p-3">
            <div className="search_input d-flex align-items-center gap-3">
              <div className="search position-relative">
                <span className="search_icon">
                  <i className="fa-solid fa-magnifying-glass"></i>
                </span>
                <input
                  type="text"
                  className="form-control h-40 search-input"
                  placeholder="Search by test name"
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
                {/* Show debouncing indicator */}
                {searchTerm !== debouncedSearchTerm && (
                  <span className="position-absolute top-50 end-0 translate-middle-y me-2">
                    <i className="fas fa-circle-notch fa-spin text-primary"></i>
                  </span>
                )}
              </div>

              <div className="date_box">
                <select
                  className="form-select status-select form-control h-40"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="btn_sec d-flex align-items-center gap-2">
              <button
                className="btn sky_theme btn-sm h-40"
                onClick={handleApplyFilters}
                disabled={loading}
              >
                Apply
              </button>

              <button
                className="btn d-flex align-items-center gap-2 font_14 clr_sky"
                onClick={handleResetFilters}
                disabled={loading}
              >
                <i className="fas fa-redo-alt"></i> Reset
              </button>

              {/* <button
                className="btn sky_border btn-sm h-40 text-nowrap d-flex align-items-center gap-2"
                onClick={handleExportResults}
                disabled={loading}
              >
                <i className="fas fa-download"></i> Export Results
              </button> */}
            </div>
          </div>

          {/* ===== Paginated Table ===== */}
          <PaginatedTable
            data={testCases}
            columns={columns}
            totalPages={Math.ceil(totalTestCases / limit)}
            currentPage={page}
            onPageChange={setPage}
            limit={limit}
            setLimit={setLimit}
            loading={loading}
            showPagination={true}
            emptyMessage="No test cases found"
          />
        </div>
      </Container>
      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

/* ================= Reusable Stat Card ================= */
const StatCard = ({ title, value, color }) => (
  <div className="col-md">
    <div className="stat-card white_box">
      <div className={`stat-icon ${color}`}>
        <svg width="17" height="18" viewBox="0 0 17 18" fill="currentColor">
          <path d="M8.25 0L9.3998 6.11501C9.59244 7.13949 10.4005 7.93691 11.4275 8.1159L16.5 9L11.4275 9.8841C10.4005 10.0631 9.59244 10.8605 9.3998 11.885L8.25 18L7.1002 11.885C6.90757 10.8605 6.09946 10.0631 5.07251 9.8841L0 9L5.07251 8.1159C6.09947 7.93691 6.90757 7.13949 7.1002 6.11501L8.25 0Z" />
        </svg>
      </div>
      <div className="stat_content">
        <p className="font_14 font-500 dgrey mb-2">{title}</p>
        <h2 className="font_20 font_600 clr_blue">{value}</h2>
      </div>
    </div>
  </div>
);

export default TestRunView;